/**
 * The product detail "headline" price — the big number at the top of the
 * pricing block (PricingInfo) — as one set of pure functions.
 *
 * PricingInfo renders it and the server uses it for the Product JSON-LD offer,
 * so the structured-data price is exactly what the page shows (Google requires
 * the marked-up price to match the visible one). Keep the rules here; never
 * re-derive a "lowest price" elsewhere for display.
 *
 * Rules (unchanged from PricingInfo, 07-17 / 08-17 / 09-22 decisions):
 *  - timeslot v2 (rental_unit=timeslot + matrix): lowest > 0 cell
 *  - daily v2 (rental_unit=daily + day tiers): lowest tier; with a base-plus-extra
 *    pricing source, the lowest 1-day (base) tier instead
 *  - v1: the locale's tiers (ko fallback), lowest per-day (daily) / price (timeslot)
 */
import type { ProductDetail, ProductPricing } from "@/types";
import {
  buildTimeslotV2PriceGroups,
  mergeTimeslotMatrixConfig,
  type TimeslotV2PriceGroup,
} from "@/lib/utils/timeslot-matrix";
import {
  buildDailyV2PriceGroups,
  mergeDailyMatrixConfig,
  type DailyV2PriceGroup,
} from "@/lib/utils/daily-matrix";

export type RentalUnit = "daily" | "timeslot";

/** v1 tiers shown for this locale (ko / unlabeled rows as fallback), in display order. */
export function selectV1PricingTiers(
  pricing: ProductPricing[],
  rentalUnit: RentalUnit,
  locale: string,
): ProductPricing[] {
  const type = rentalUnit === "daily" ? "daily" : "timeslot";
  let localePricing = pricing.filter(
    (p) => p.pricing_type === type && p.lang_code === locale,
  );
  if (localePricing.length === 0) {
    localePricing = pricing.filter(
      (p) => p.pricing_type === type && (p.lang_code === "ko" || !p.lang_code),
    );
  }
  return rentalUnit === "daily"
    ? localePricing.sort((a, b) => (a.min_days || 0) - (b.min_days || 0))
    : localePricing.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
}

/** The v1 tier the headline shows (first lowest), or null when there are no tiers. */
export function pickV1HeadlineTier(
  tiers: ProductPricing[],
  rentalUnit: RentalUnit,
): ProductPricing | null {
  if (tiers.length === 0) return null;
  const priceOf = (p: ProductPricing) =>
    (rentalUnit === "daily" ? p.price_per_day : p.price) || 0;
  return tiers.reduce(
    (lowest, current) => (priceOf(current) < priceOf(lowest) ? current : lowest),
    tiers[0],
  );
}

export function v1TierPrice(tier: ProductPricing, rentalUnit: RentalUnit): number {
  return (rentalUnit === "daily" ? tier.price_per_day : tier.price) || 0;
}

/** Timeslot v2 headline cell: the first lowest row across groups. */
export function pickTimeslotV2Headline(
  groups: TimeslotV2PriceGroup[],
): { price: number; groupName: string | null; rowLabel: string } | null {
  let best: { price: number; groupName: string | null; rowLabel: string } | null = null;
  for (const group of groups) {
    for (const row of group.rows) {
      if (!best || row.price < best.price) {
        best = { price: row.price, groupName: group.name, rowLabel: row.label };
      }
    }
  }
  return best;
}

export interface DailyV2Headline {
  price: number;
  /** The sub-product (price group) the headline comes from */
  groupId: string;
  groupName: string | null;
  minDays: number;
  isBase: boolean;
}

/**
 * Daily v2 headline tier. With a base-plus-extra pricing source the headline is
 * the cheapest 1-day (base) tier — a "60 days+" unit price means nothing there.
 */
export function pickDailyV2Headline(
  groups: DailyV2PriceGroup[],
  hasPricingSource: boolean,
): DailyV2Headline | null {
  if (hasPricingSource) {
    let base: DailyV2Headline | null = null;
    for (const group of groups) {
      const dayOne = group.rows.find((row) => row.minDays === 1);
      if (dayOne && dayOne.price > 0 && (!base || dayOne.price < base.price)) {
        base = { price: dayOne.price, groupId: group.id, groupName: group.name, minDays: 1, isBase: true };
      }
    }
    if (base) return base;
  }
  let best: DailyV2Headline | null = null;
  for (const group of groups) {
    for (const row of group.rows) {
      if (!best || row.price < best.price) {
        best = { price: row.price, groupId: group.id, groupName: group.name, minDays: row.minDays, isBase: false };
      }
    }
  }
  return best;
}

/**
 * Calendar days the base fare covers for the headline's sub-product (1 or 2) —
 * the headline "/ 기본 요금" must say "(2일)" when the big number is a 2-day
 * price (10-15). Null when the headline isn't a base fare or the source has no
 * row for it.
 */
export function headlineBaseDays(
  headline: Pick<DailyV2Headline, "groupId" | "isBase"> | null,
  subProducts: ReadonlyArray<{ id: string; displayOrder: number }>,
  pricingSource: { subProducts: Record<string, { baseDays?: 1 | 2 }> } | null | undefined,
): 1 | 2 | null {
  if (!headline?.isBase || !pricingSource) return null;
  const sub = subProducts.find((s) => s.id === headline.groupId);
  const source = sub ? pricingSource.subProducts[String(sub.displayOrder)] : undefined;
  if (!source) return null;
  return source.baseDays === 1 ? 1 : 2;
}

type HeadlineProduct = Pick<
  ProductDetail,
  | "name"
  | "pricing"
  | "rental_unit"
  | "pricingSource"
  | "timeslot_version"
  | "time_options"
  | "sub_products"
  | "sub_product_prices"
  | "daily_version"
  | "day_prices"
>;

/**
 * The headline price in the product's own currency, built from the product the
 * detail page receives (same merges as ProductDetailClient → PricingInfo).
 * Returns null when the page shows no price block.
 */
export function getProductHeadlinePrice(
  product: HeadlineProduct,
  locale: string,
): number | null {
  const rentalUnit: RentalUnit = product.rental_unit === "timeslot" ? "timeslot" : "daily";

  if (rentalUnit === "timeslot" && product.timeslot_version === 2) {
    const matrix = mergeTimeslotMatrixConfig(
      {
        timeOptionRows: product.time_options || [],
        subProductRows: product.sub_products || [],
        priceRows: product.sub_product_prices || [],
      },
      locale,
      "ko",
    );
    const v2 = pickTimeslotV2Headline(buildTimeslotV2PriceGroups(matrix, product.name));
    if (v2) return v2.price;
  }

  if (rentalUnit === "daily" && product.daily_version === 2) {
    const dailyMatrix = mergeDailyMatrixConfig(
      {
        subProductRows: product.sub_products || [],
        dayPriceRows: product.day_prices || [],
      },
      locale,
      "ko",
    );
    const daily = pickDailyV2Headline(
      buildDailyV2PriceGroups(dailyMatrix.subProducts, product.name),
      !!product.pricingSource,
    );
    if (daily) return daily.price;
  }

  const tier = pickV1HeadlineTier(
    selectV1PricingTiers(product.pricing || [], rentalUnit, locale),
    rentalUnit,
  );
  const price = tier ? v1TierPrice(tier, rentalUnit) : 0;
  return price > 0 ? price : null;
}
